import React, { useState, useEffect } from 'react';
import { FaCookieBite, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const COOKIE_CONSENT_KEY = 'itineraryseek_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'itineraryseek_cookie_preferences';
const USER_COUNTRY_KEY = 'itineraryseek_user_country';

// EU/EEA countries that require GDPR opt-in consent
const GDPR_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU members
  'IS', 'LI', 'NO', // EEA
  'GB', 'CH' // UK (post-Brexit GDPR) and Switzerland
];

// Default preferences for non-GDPR countries (opt-out model)
const DEFAULT_PREFERENCES_OPTOUT = {
  necessary: true,
  analytics: true,
  marketing: true,
  personalization: true,
};

// Default preferences for GDPR countries (opt-in model)
const DEFAULT_PREFERENCES_OPTIN = {
  necessary: true,
  analytics: false,
  marketing: false,
  personalization: false,
};

function CookieConsent({ onConsentChange }) {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES_OPTOUT);
  const [isGdprCountry, setIsGdprCountry] = useState(false);
  const [countryDetected, setCountryDetected] = useState(false);

  // Detect user's country for GDPR compliance
  useEffect(() => {
    const detectCountry = async () => {
      // Check cached country first
      const cachedCountry = localStorage.getItem(USER_COUNTRY_KEY);
      if (cachedCountry) {
        const isGdpr = GDPR_COUNTRIES.includes(cachedCountry);
        setIsGdprCountry(isGdpr);
        setPreferences(isGdpr ? DEFAULT_PREFERENCES_OPTIN : DEFAULT_PREFERENCES_OPTOUT);
        setCountryDetected(true);
        return cachedCountry;
      }

      // Detect country via free IP geolocation API
      try {
        const response = await fetch('https://ipapi.co/json/', { timeout: 3000 });
        if (response.ok) {
          const data = await response.json();
          const country = data.country_code || 'US';
          localStorage.setItem(USER_COUNTRY_KEY, country);
          const isGdpr = GDPR_COUNTRIES.includes(country);
          setIsGdprCountry(isGdpr);
          setPreferences(isGdpr ? DEFAULT_PREFERENCES_OPTIN : DEFAULT_PREFERENCES_OPTOUT);
          setCountryDetected(true);
          return country;
        }
      } catch (error) {
        console.log('Country detection failed, defaulting to US rules');
      }

      // Default to US (opt-out) if detection fails
      setCountryDetected(true);
      return 'US';
    };

    detectCountry();
  }, []);

  useEffect(() => {
    if (!countryDetected) return;

    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);

      // For non-GDPR countries, auto-enable tracking (opt-out model)
      // For GDPR countries, wait for explicit consent (opt-in model)
      if (!isGdprCountry && onConsentChange) {
        onConsentChange(DEFAULT_PREFERENCES_OPTOUT);
      }
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        const prefs = JSON.parse(savedPrefs);
        setPreferences(prefs);
        // Notify parent of existing consent
        if (onConsentChange) {
          onConsentChange(prefs);
        }
      }
    }
  }, [onConsentChange, countryDetected, isGdprCountry]);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(prefs);
    setShowBanner(false);

    // Notify parent component
    if (onConsentChange) {
      onConsentChange(prefs);
    }

    // Dispatch custom event for tracking scripts to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: prefs }));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    saveConsent(allAccepted);
  };

  const handleAcceptNecessary = () => {
    // "Necessary Only" means all non-essential cookies are OFF
    saveConsent(DEFAULT_PREFERENCES_OPTIN);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
        {/* Main Banner */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-3 bg-primary-100 rounded-full">
              <FaCookieBite className="text-2xl text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-neutral-800 mb-2">
                We Value Your Privacy
              </h3>
              <p className="text-neutral-600 text-sm mb-4">
                We use cookies to enhance your browsing experience, serve personalized ads or content,
                and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                You can customize your preferences below.
              </p>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Accept All
                </button>
                <button
                  onClick={handleAcceptNecessary}
                  className="px-6 py-2.5 bg-neutral-200 text-neutral-700 font-semibold rounded-lg hover:bg-neutral-300 transition-colors"
                >
                  Necessary Only
                </button>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="px-4 py-2.5 text-primary-600 font-medium flex items-center gap-2 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  Customize {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>

              {/* Detailed Preferences */}
              {showDetails && (
                <div className="border-t border-neutral-200 pt-4 mt-4 space-y-4">
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-neutral-800">Necessary Cookies</h4>
                      <p className="text-sm text-neutral-500">
                        Required for the website to function. Cannot be disabled.
                      </p>
                    </div>
                    <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      Always On
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-neutral-800">Analytics Cookies</h4>
                      <p className="text-sm text-neutral-500">
                        Help us understand how visitors interact with our website.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-primary-600' : 'bg-neutral-300'
                      }`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.analytics ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-neutral-800">Marketing Cookies</h4>
                      <p className="text-sm text-neutral-500">
                        Used to deliver personalized ads and measure ad performance.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-primary-600' : 'bg-neutral-300'
                      }`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.marketing ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Personalization Cookies */}
                  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-neutral-800">Personalization Cookies</h4>
                      <p className="text-sm text-neutral-500">
                        Remember your preferences for a personalized experience.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('personalization')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.personalization ? 'bg-primary-600' : 'bg-neutral-300'
                      }`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.personalization ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSavePreferences}
                      className="px-6 py-2.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="text-xs text-neutral-500 mt-4">
                By using our site, you agree to our{' '}
                <a href="/privacy" className="text-primary-600 hover:underline">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="/terms" className="text-primary-600 hover:underline">
                  Terms of Service
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Check if user is in a GDPR country
function isUserInGdprCountry() {
  const country = localStorage.getItem(USER_COUNTRY_KEY);
  return country ? GDPR_COUNTRIES.includes(country) : false;
}

// Utility function to check if marketing cookies are allowed
// Returns TRUE by default for non-GDPR, FALSE for GDPR countries
export function hasMarketingConsent() {
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!prefs) {
    // No explicit consent yet - use geo-based default
    return !isUserInGdprCountry();
  }
  try {
    return JSON.parse(prefs).marketing === true;
  } catch {
    return !isUserInGdprCountry();
  }
}

// Utility function to check if analytics cookies are allowed
// Returns TRUE by default for non-GDPR, FALSE for GDPR countries
export function hasAnalyticsConsent() {
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!prefs) {
    // No explicit consent yet - use geo-based default
    return !isUserInGdprCountry();
  }
  try {
    return JSON.parse(prefs).analytics === true;
  } catch {
    return !isUserInGdprCountry();
  }
}

export default CookieConsent;
